import React, { useState } from 'react';
import { useStore } from '../../stores';
import { ChevronDown, Settings, Plus, Check, Globe, Box } from 'lucide-react';

interface EnvironmentSelectorProps {
  onEditEnvironment?: (environmentId: string) => void;
  onCreateEnvironment?: () => void;
}

export function EnvironmentSelector({ onEditEnvironment, onCreateEnvironment }: EnvironmentSelectorProps) {
  const { environments, activeEnvironmentId, setActiveEnvironment } = useStore();
  const [isOpen, setIsOpen] = useState(false);

  const activeEnvironment = environments.find((env) => env.id === activeEnvironmentId);

  const handleSelect = (envId: string | null) => {
    setActiveEnvironment(envId);
    setIsOpen(false);
  };

  const handleEdit = (e: React.MouseEvent, envId: string) => {
    e.stopPropagation();
    onEditEnvironment?.(envId);
    setIsOpen(false);
  };

  return (
    <div className="relative">
      {/* Trigger Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-1.5 text-sm bg-background border border-input rounded-md hover:bg-accent focus:outline-none focus:ring-2 focus:ring-ring transition-colors min-w-[160px]"
      >
        {activeEnvironment ? (
          <>
            {activeEnvironment.isGlobal ? (
              <Globe className="w-4 h-4 text-blue-500" />
            ) : (
              <Box className="w-4 h-4 text-green-500" />
            )}
            <span className="flex-1 text-left truncate">{activeEnvironment.name}</span>
          </>
        ) : (
          <>
            <Box className="w-4 h-4 text-muted-foreground" />
            <span className="flex-1 text-left text-muted-foreground">No Environment</span>
          </>
        )}
        <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <>
          {/* Backdrop to close dropdown */}
          <div
            className="fixed inset-0 z-40"
            onClick={() => setIsOpen(false)}
          />

          <div className="absolute top-full mt-1 left-0 w-64 bg-card border border-border rounded-md shadow-lg z-50 overflow-hidden">
            {/* No Environment Option */}
            <div
              onClick={() => handleSelect(null)}
              className="flex items-center gap-2 px-3 py-2 hover:bg-accent cursor-pointer group"
            >
              <Box className="w-4 h-4 text-muted-foreground" />
              <span className="flex-1 text-sm text-muted-foreground">No Environment</span>
              {!activeEnvironmentId && (
                <Check className="w-4 h-4 text-primary" />
              )}
            </div>

            {environments.length > 0 && (
              <>
                <div className="h-px bg-border" />

                {/* Environment List */}
                <div className="max-h-60 overflow-y-auto">
                  {environments.map((env) => (
                    <div
                      key={env.id}
                      onClick={() => handleSelect(env.id)}
                      className="flex items-center gap-2 px-3 py-2 hover:bg-accent cursor-pointer group"
                    >
                      {env.isGlobal ? (
                        <Globe className="w-4 h-4 text-blue-500" />
                      ) : (
                        <Box className="w-4 h-4 text-green-500" />
                      )}
                      <div className="flex-1 min-w-0">
                        <span className="text-sm text-foreground truncate block">{env.name}</span>
                        {env.variables.length > 0 && (
                          <span className="text-xs text-muted-foreground">
                            {env.variables.length} variable{env.variables.length !== 1 ? 's' : ''}
                          </span>
                        )}
                      </div>
                      {activeEnvironmentId === env.id && (
                        <Check className="w-4 h-4 text-primary flex-shrink-0" />
                      )}
                      {onEditEnvironment && (
                        <button
                          onClick={(e) => handleEdit(e, env.id)}
                          className="p-1 rounded opacity-0 group-hover:opacity-100 hover:bg-background transition-all flex-shrink-0"
                          title="Edit environment"
                        >
                          <Settings className="w-3.5 h-3.5 text-muted-foreground" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </>
            )}

            {/* Create New Environment */}
            {onCreateEnvironment && (
              <>
                <div className="h-px bg-border" />
                <button
                  onClick={() => {
                    onCreateEnvironment();
                    setIsOpen(false);
                  }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-primary hover:bg-accent transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  New Environment
                </button>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}
